import { ChangeTyped, Query, QueryService, SearchResult } from '@damofire/data-commons';
import { cloneDeep } from 'lodash-es';
import { defer, Observable, of, switchMap } from 'rxjs';
import { Asset, Column, System2d, Upright } from '../asset/asset.dto';
import { ASSET_SEARCH_FIELDS } from '../asset/asset-observable.service';
import { ISSUE_SEARCH_FIELDS } from '../issue/issue-observable.service';
import { Issue, IssueMedia, Resolution } from '../issue/issue.dto';
import { Note } from '../note/note.dto';
import { WarehouseStateSnapshotPayload } from '../snapshot/warehouse-state-snapshot.dto';
import { LayoutTextSegment, WarehouseOutlineSegment } from '../warehouse/warehouse.dto';
import { HistoricalViewState } from './historical-view-state';

/**
 * The snapshot side of every history-aware read, shared by the Firebase (online) and Idb (offline)
 * wrappers so both platforms serve exactly the same historical state. Pure functions of the
 * payload: none of them touch Firestore or IndexedDB.
 */

/** Pages the in-memory payload with the same engine the live search already runs on. */
const searchPayload = <T>(rows: T[], query: Query, searchFields?: string[]): SearchResult<T> => QueryService.searchCollection<T>(cloneDeep(rows ?? []), query, searchFields);

const attachNotes = (issues: Issue[], notes: Note[]): Issue[] =>
  issues.map((issue: Issue) => ({ ...cloneDeep(issue), notes: (notes ?? []).filter((note: Note) => note.issue?._id === issue._id) }));

const asAdded = <T>(rows: T[]): ChangeTyped<T>[] => rows.map((row: T) => ({ ...cloneDeep(row), changeType: 'added' }) as ChangeTyped<T>);

/**
 * Picks between snapshot data and live data for an observable read.
 *
 * If no snapshot is selected for this division, it returns the live stream directly.
 *
 * If a snapshot is selected, it waits for the snapshot's payload to finish downloading, then emits
 * the result of `fromPayload` once. A snapshot never changes, so there is nothing more to listen for.
 * If the download fails, or the user goes back to the current state while waiting, it falls back to
 * the live stream instead.
 */
export const fromHistory = <T>(
  history: HistoricalViewState,
  divisionId: string,
  fromPayload: (payload: WarehouseStateSnapshotPayload) => T,
  live: () => Observable<T>,
): Observable<T> =>
  history.isActive(divisionId)
    ? defer(() => history.payloadReady(divisionId)).pipe(switchMap((payload: WarehouseStateSnapshotPayload | null) => (payload ? of(fromPayload(payload)) : live())))
    : live();

/** Promise counterpart of fromHistory: waits for an in-flight payload, and goes live without one. */
export const readFromHistory = async <T>(
  history: HistoricalViewState,
  divisionId: string,
  fromPayload: (payload: WarehouseStateSnapshotPayload) => T,
  live: () => Promise<T>,
): Promise<T> => {
  const payload = await history.payloadReady(divisionId);

  return payload ? fromPayload(payload) : live();
};

/**
 * Reloads a WarehouseService layout cache (outlines or texts) from whichever source is selected.
 *
 * The generation is captured before waiting, so a switch during the wait still counts: a live read
 * that outlives a source switch has just overwritten the newer source's cache, so it loads again.
 */
export const reloadLayoutFromHistory = async (
  history: HistoricalViewState,
  divisionId: string,
  fromPayload: (payload: WarehouseStateSnapshotPayload) => void,
  live: () => Promise<void>,
  reload: () => Promise<void>,
): Promise<void> => {
  const generation = history.generation;
  const payload = await history.payloadReady(divisionId);

  if (!payload) {
    await live();

    return generation === history.generation ? undefined : reload();
  }

  fromPayload(payload);
};

export const snapshotReads = {
  issueSearch: (payload: WarehouseStateSnapshotPayload, query: Query): SearchResult<Issue> =>
    searchPayload<Issue>(attachNotes(payload.issues ?? [], payload.notes), query, ISSUE_SEARCH_FIELDS),

  issue: (payload: WarehouseStateSnapshotPayload, id: string): Issue => {
    const issue = payload.issues?.find((candidate: Issue) => String(candidate._id) === String(id));

    return issue ? cloneDeep(issue) : null;
  },

  openIssues: (payload: WarehouseStateSnapshotPayload, section?: string): ChangeTyped<Issue>[] =>
    asAdded((payload.issues ?? []).filter((issue: Issue) => issue.resolution === Resolution.OPEN && (!section || issue.asset?.section === section))),

  inspectionIssues: (payload: WarehouseStateSnapshotPayload, inspectionId: string): ChangeTyped<Issue>[] => {
    const matchesSnapshot = !!payload.inspection?._id && String(payload.inspection._id) === String(inspectionId);

    return asAdded(
      (payload.issues ?? []).filter((issue: Issue) => (matchesSnapshot ? String(issue.inspection?._id) === String(payload.inspection._id) : issue.resolution === Resolution.OPEN)),
    );
  },

  asset: (payload: WarehouseStateSnapshotPayload, id: string | number): Asset => {
    const asset = payload.assets?.find((candidate: Asset) => String(candidate._id) === String(id));

    return asset ? cloneDeep(asset) : null;
  },

  assetObservation: (payload: WarehouseStateSnapshotPayload, id: string): ChangeTyped<Asset> => {
    const asset = (payload.assets ?? []).find((candidate: Asset) => String(candidate._id) === String(id));

    return asset ? ({ ...cloneDeep(asset), changeType: 'added' } as ChangeTyped<Asset>) : ({ _id: id, changeType: 'removed' } as unknown as ChangeTyped<Asset>);
  },

  assetSearch: (payload: WarehouseStateSnapshotPayload, query: Query): SearchResult<Asset> => searchPayload<Asset>(payload.assets, query, ASSET_SEARCH_FIELDS),

  upright: (payload: WarehouseStateSnapshotPayload, assetId: string): Upright => {
    const upright = payload.warehouseAssets?.find((candidate: Upright) => String(candidate.assetId) === String(assetId));

    return upright ? cloneDeep(upright) : null;
  },

  uprights: (payload: WarehouseStateSnapshotPayload, systemId: string): Upright[] =>
    cloneDeep((payload.warehouseAssets ?? []).filter((upright: Upright) => upright.systemId === systemId)),

  columns: (payload: WarehouseStateSnapshotPayload, assetIds: string[], rowIndex?: number): Column[] => {
    // Copy first: the live implementation drains the array it is given, and callers rely on that.
    const wanted = new Set<string>([...assetIds].map(String));

    return cloneDeep((payload.columns ?? []).filter((column: Column) => wanted.has(String(column.assetId)) && (!rowIndex || column.rowIndex === rowIndex)));
  },

  system: (payload: WarehouseStateSnapshotPayload, systemId: string): System2d => {
    const found = payload.systems?.find((candidate: System2d) => candidate._id === systemId);

    return found ? cloneDeep(found) : null;
  },

  /** The systems in a section, each joined with its uprights, sorted the way the live list is. */
  systems: (payload: WarehouseStateSnapshotPayload, section: string | null): System2d[] => {
    const systemsInSection: System2d[] = (payload.systems ?? []).filter((system: System2d & { section?: string }) => !section || system.section === section);
    const systemIds = new Set<string>(systemsInSection.map((system: System2d) => String(system._id)));
    const uprightsBySystem = new Map<string, Upright[]>();

    for (const upright of payload.warehouseAssets ?? []) {
      const systemId = String(upright.systemId);
      if (!systemIds.has(systemId)) {
        continue;
      }

      const systemUprights = uprightsBySystem.get(systemId);
      if (systemUprights) {
        systemUprights.push(upright);
      } else {
        uprightsBySystem.set(systemId, [upright]);
      }
    }

    const systems = systemsInSection.map((system: System2d) => ({
      ...system,
      uprights: (uprightsBySystem.get(String(system._id)) ?? []).sort((a: Upright, b: Upright) => a.index - b.index),
    }));

    return cloneDeep([...systems].sort((a: System2d, b: System2d): number => a.number - b.number));
  },

  outlineSearch: (payload: WarehouseStateSnapshotPayload): SearchResult<WarehouseOutlineSegment> => searchPayload<WarehouseOutlineSegment>(payload.outline, { filters: {} }),

  layoutTextSearch: (payload: WarehouseStateSnapshotPayload): SearchResult<LayoutTextSegment> => searchPayload<LayoutTextSegment>(payload.layoutTexts, { filters: {} }),

  outline: (payload: WarehouseStateSnapshotPayload): WarehouseOutlineSegment[] => cloneDeep(payload.outline ?? []),

  layoutTexts: (payload: WarehouseStateSnapshotPayload): LayoutTextSegment[] => cloneDeep(payload.layoutTexts ?? []),

  divisionNotes: (payload: WarehouseStateSnapshotPayload): Note[] => cloneDeep(payload.notes ?? []),

  notes: (payload: WarehouseStateSnapshotPayload, inspectionId: string, assetId?: string): Note[] =>
    cloneDeep(
      (payload.notes ?? []).filter(
        (note: Note) => (!assetId || String(note.asset?._id) === String(assetId)) && (!inspectionId || String(note.inspection?._id) === String(inspectionId)),
      ),
    ),

  issueNotes: (payload: WarehouseStateSnapshotPayload, issueId: string): Note[] =>
    cloneDeep((payload.notes ?? []).filter((note: Note) => String(note.issue?._id) === String(issueId))),

  // Media metadata comes from the snapshot; the bytes themselves are still fetched on demand from
  // storage, which is what keeps historical viewing free of local copies.
  issueMedia: <T extends IssueMedia>(payload: WarehouseStateSnapshotPayload, issueId: string): ChangeTyped<T>[] =>
    asAdded((payload.pictures ?? []).filter((picture: IssueMedia) => String(picture.issue?._id) === String(issueId)) as T[]),
};
